import { useAtomValue } from 'jotai';

import { ListViewGroup, ListViewRow, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { Submission } from '@common/components/LenderDecisionsList';

import { AccordionSection } from '@common/components/AccordionSection';
import { DetailedInfoLineItem } from '@common/components/DetailedInfoLineItem';
import { EmptySection } from '@common/components/EmptySection';
import { FicoScoreDisplay } from '@common/components/FicoScoreDisplay';
import { InfoLineItem } from '@common/components/InfoLineItem';
import { LenderDecisionsList } from '@common/components/LenderDecisionsList';
import { formatCurrency } from '@common/lib/currency';

import { useGetOrderPaymentFai } from '../../api/useGetOrderPaymentFai';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';
import { SectionSkeleton } from '../order-details/shared/SectionSkeleton';

const SECTION_TITLE = 'Financing';

interface RequestedTerms {
	termMonths?: number;
	downPayment?: number;
	financedAmount?: number;
}

interface Application {
	requestedTerms?: RequestedTerms;
	ficoScore?: number;
	ficoScoreRating?: string;
}

interface FinancingDetailsProps {
	application?: Application;
	lenderDecisions: Submission[];
}

const buildRequestedTermsSubtitle = (application?: Application): string => {
	const parts: string[] = [];
	const { requestedTerms } = application || {};

	if (requestedTerms?.termMonths) {
		parts.push(`${requestedTerms.termMonths}-month term`);
	}

	if (requestedTerms?.downPayment !== undefined) {
		parts.push(formatCurrency(requestedTerms.downPayment) + ' down');
	}

	if (requestedTerms?.financedAmount !== undefined) {
		parts.push(formatCurrency(requestedTerms.financedAmount) + ' financed');
	}

	return parts.join(' • ');
};

/**
 * Shared component that renders financing application and submission details.
 * Used in both the order details drawer and the financing cell popover.
 */
export const FinancingDetails = ({ application, lenderDecisions }: FinancingDetailsProps) => {
	return (
		<Stack itemsSpacing='12'>
			<Text size='footnote' weight='semibold'>
				Application
			</Text>
			{application?.requestedTerms ? (
				<ListViewGroup aria-label='Application'>
					<ListViewRow
						key={'application'}
						bottomContent={
							<Stack itemsSpacing='8'>
								<DetailedInfoLineItem
									primaryLabel='Requested terms'
									secondaryLabel={buildRequestedTermsSubtitle(application)}
								/>
								{application.ficoScore !== undefined && (
									<InfoLineItem
										title='FICO score'
										content={
											<FicoScoreDisplay
												ficoScore={application.ficoScore}
												ficoScoreRating={application.ficoScoreRating}
											/>
										}
									/>
								)}
							</Stack>
						}
					/>
				</ListViewGroup>
			) : (
				<EmptySection message='No credit application on this order' />
			)}
			<Text size='footnote' weight='semibold'>
				Lender decisions
			</Text>
			{lenderDecisions.length > 0 ? (
				<LenderDecisionsList lenderDecisions={lenderDecisions} />
			) : (
				<EmptySection message='No lender decisions on this order' />
			)}
		</Stack>
	);
};

export const FinancingSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderPaymentFai, isLoading } = useGetOrderPaymentFai(
		selectedRow?.purchasedVehicleId || undefined,
	);

	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!orderPaymentFai) {
		return null;
	}

	const { financing } = orderPaymentFai;
	const { application, lenderDecisions } = financing;

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<FinancingDetails application={application} lenderDecisions={lenderDecisions} />
		</AccordionSection>
	);
};
